import { breakpoints } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { useSnowplowTracking, useSiteContext, useProperty, useSearch } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { m as motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { SearchForm } from '../../../../widgets/search-form';
import MoveToTopButton from '../MoveToTopButton';
import type { RebookingData, SearchTypes } from '../../models';
import ResultTripDetailsHeader from './ResultTripDetailsHeader';

const searchFormWrapperStyles = css`
  font-size: 0.875rem;
  padding-block: 16px 24px;
  width: 100%;

  @media (min-width: ${breakpoints._1024}) {
    max-width: 950px;
  }
`;

const sectionStyles = (isAlternateSearchBarEnabled: boolean) => css`
  background-color: ${isAlternateSearchBarEnabled ? etiColors.white : etiColors.grayScale[2]};

  @media (min-width: ${breakpoints._768}) {
    box-shadow:
      0 4px 8px rgb(0 0 0 / 4%),
      0 0 2px rgb(0 0 0 / 6%),
      0 0 1px rgb(0 0 0 / 4%);
    width: 100%;
    z-index: 11;
  }
`;

const motionVariants = {
  show: {
    height: 'auto',
    transitionEnd: { overflow: 'visible' },
  },
  hide: {
    overflow: 'hidden',
    height: 0,
  },
};

export interface ResultSearchDetailsProps {
  isSearchPanelOpen: boolean;
  rebookingData: RebookingData;
  search: SearchTypes;
  shouldShowSkeleton: boolean;
  toggleSearchPanel: () => void;
  closeSearchPanel: () => void;
  onSearch: (arg: boolean) => void;
}

const ResultSearchDetails = ({
  closeSearchPanel,
  isSearchPanelOpen,
  onSearch,
  rebookingData,
  search,
  shouldShowSkeleton = false,
  toggleSearchPanel,
}: ResultSearchDetailsProps) => {
  const { language } = useSiteContext();
  const { p } = useProperty();
  const { ref, inView } = useInView({ rootMargin: '1650px' });
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const isAlternateSearchBarEnabled = p('Result.AlternateSearchBar.Enabled');
  const { isSearchFormOpen, setIsSearchFormOpen } = useSearch();
  const shouldShowSearchForm = isSearchPanelOpen || isSearchFormOpen;

  const handleToggleSearchBar = () => {
    if (isSnowplowTrackingEnabled && !isSearchPanelOpen) {
      sendTrackEvent({
        category: 'Results',
        action: 'Click',
        label: 'Search-bar',
      });
    }

    toggleSearchPanel();

    if (isAlternateSearchBarEnabled) {
      setIsSearchFormOpen!(!isSearchFormOpen);
    }
  };

  const handleSearch = (values: boolean) => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Results',
        action: 'Click',
        label: 'Search-button',
      });
    }

    onSearch(values);
    closeSearchPanel();

    if (isAlternateSearchBarEnabled) {
      setIsSearchFormOpen!(!isSearchFormOpen);
    }
  };

  return (
    <section className={sectionStyles(isAlternateSearchBarEnabled)} ref={ref}>
      <ContentWrapper>
        <ResultTripDetailsHeader
          isOpen={shouldShowSearchForm}
          onClick={handleToggleSearchBar}
          shouldShowSkeleton={shouldShowSkeleton}
        />
        {!shouldShowSkeleton && (
          <motion.div
            animate={shouldShowSearchForm ? 'show' : 'hide'}
            data-testid="resultPage-searchForm-content"
            initial={false}
            transition={{
              type: 'tween',
              duration: 0.3,
            }}
            variants={motionVariants}
          >
            <div className={searchFormWrapperStyles}>
              <SearchForm
                language={language.code}
                onSearch={handleSearch}
                rebookingData={rebookingData}
                search={search}
                shouldShowRebookingBanner={false}
              />
            </div>
          </motion.div>
        )}
      </ContentWrapper>

      {p('Result.MoveToTopButton.Enabled') && <MoveToTopButton isVisible={!inView} />}
    </section>
  );
};

export default ResultSearchDetails;
